import { useRef, type CSSProperties } from "react";

import { VK_SPANNE } from "@repo/shared";

type Props = {
  von: number;
  bis: number;
  /** Laufend beim Ziehen – für die Beschriftung. */
  onChange(von: number, bis: number): void;
  /** Beim Loslassen bzw. nach einer Taste, nur wenn sich etwas bewegt hat. */
  onFertig(von: number, bis: number): void;
};

const { min, max, schritt, mindestAbstand } = VK_SPANNE;
const prozent = (wert: number) => `${((wert - min) / (max - min)) * 100}%`;

/**
 * Doppel-Schieberegler aus zwei übereinanderliegenden `input[type=range]`. Die Griffe halten
 * den Mindestabstand; nur die Griffe nehmen Klicks an, damit beide erreichbar bleiben.
 */
export function PreisSpanne({ von, bis, onChange, onFertig }: Props) {
  const bewegt = useRef(false);
  const stand = useRef({ von, bis });
  stand.current = { von, bis };

  function setzeVon(wert: number) {
    const neu = Math.max(min, Math.min(wert, bis - mindestAbstand));
    bewegt.current = true;
    stand.current = { von: neu, bis };
    onChange(neu, bis);
  }

  function setzeBis(wert: number) {
    const neu = Math.min(max, Math.max(wert, von + mindestAbstand));
    bewegt.current = true;
    stand.current = { von, bis: neu };
    onChange(von, neu);
  }

  function fertig() {
    if (!bewegt.current) return;
    bewegt.current = false;
    onFertig(stand.current.von, stand.current.bis);
  }

  const gemeinsam = {
    type: "range" as const,
    min,
    max,
    step: schritt,
    onPointerUp: fertig,
    onKeyUp: fertig,
    onBlur: fertig,
  };

  return (
    <div className="stoffe-spanne" style={{ "--von": prozent(von), "--bis": prozent(bis) } as CSSProperties}>
      <div className="stoffe-spanne__spur" aria-hidden="true" />
      <input
        {...gemeinsam}
        // Liegen beide Griffe rechts, muss „von“ oben liegen – sonst ließe er sich nicht mehr greifen.
        className={von > (min + max) / 2 ? "stoffe-spanne__griff stoffe-spanne__griff--oben" : "stoffe-spanne__griff"}
        value={von}
        aria-label="VK-Preis von"
        aria-valuetext={`${von} €`}
        onChange={(e) => setzeVon(Number(e.target.value))}
      />
      <input
        {...gemeinsam}
        className="stoffe-spanne__griff"
        value={bis}
        aria-label="VK-Preis bis"
        aria-valuetext={`${bis} €`}
        onChange={(e) => setzeBis(Number(e.target.value))}
      />
    </div>
  );
}
